import { Image } from '@cian/ui-kit';
import { Phone16, User16, Attention16 } from '@cian/ui-kit/prev';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { moveToSpam } from '../../actions/chatOperations';
import { trackClickCallButton, trackClickMoveToSpam } from '../../actions/userCard/tracking';
import { TLayoutUserCardAction, UserCardActions } from '../../components/UserCard/UserCardActions';
import { UserCardDesktopBlockedChat } from '../../components/UserCard/UserCardDesktopBlockedChat';
import { UserCardDesktopPhone } from '../../components/UserCard/UserCardDesktopPhone';
import { UserCardDesktopProfileLink } from '../../components/UserCard/UserCardDesktopProfileLink';
import { useCompanionData } from '../../hooks';
import { currentChatOpponentIdSelector, currentOpenChatSelector, customActionsSelector } from '../../selectors';
import { EAvailableActions } from '../../types/availableActions';
import { ECustomAction } from '../../types/customActions';
import { UserCardCustomActionButtonContainer } from '../UserCardCustomActionButtonContainer';

export const UserCardActionsContainer = () => {
  const dispatch = useDispatch();

  const currentChat = useSelector(currentOpenChatSelector);
  const customActions = useSelector(customActionsSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);

  const isCanMoveToSpam =
    currentChat && currentChat.availableActions
      ? currentChat.availableActions.indexOf(EAvailableActions.Block) !== -1 && !currentChat.blocked
      : false;

  const { companionId, formatPhone, profileLink } = useCompanionData(currentChat?.chatId);

  const subscribeActionButton = React.useMemo(() => {
    const button = customActions
      ? customActions.find(({ action }) => action === ECustomAction.Unsubscribe || action === ECustomAction.Subscribe)
      : undefined;

    return button || null;
  }, [customActions]);

  if (!currentChat || !companionId) {
    return null;
  }

  const handleClickCallButton = () => {
    trackClickCallButton(currentChat, companionId, opponentId);
  };

  const handleClickSpam = () => {
    dispatch(moveToSpam(currentChat.chatId));
    trackClickMoveToSpam(currentChat, companionId, opponentId);
  };

  const actions = [];

  if (subscribeActionButton) {
    actions.push({
      icon: (
        <Image
          height={16}
          borderRadius="0"
          objectFit="contain"
          src={subscribeActionButton.desktopIconUrl}
          data-testid="image"
        />
      ),
      content: <UserCardCustomActionButtonContainer button={subscribeActionButton} chatId={currentChat.chatId} />,
    } as TLayoutUserCardAction);
  }

  if (formatPhone) {
    actions.push({
      icon: <Phone16 color="gray_icons_100" />,
      content: <UserCardDesktopPhone phone={formatPhone} onClick={handleClickCallButton} />,
    } as TLayoutUserCardAction);
  }

  if (profileLink) {
    actions.push({
      icon: <User16 color="gray_icons_100" />,
      content: <UserCardDesktopProfileLink userProfileUrl={profileLink} />,
    } as TLayoutUserCardAction);
  }

  if (isCanMoveToSpam) {
    actions.push({
      icon: <Attention16 color="gray_icons_100" />,
      content: <UserCardDesktopBlockedChat handleClickSpam={handleClickSpam} />,
    } as TLayoutUserCardAction);
  }

  if (customActions) {
    customActions.forEach(button => {
      if (button.action !== ECustomAction.Unsubscribe && button.action !== ECustomAction.Subscribe) {
        actions.push({
          icon: (
            <Image height={16} borderRadius="0" objectFit="contain" src={button.desktopIconUrl} data-testid="image" />
          ),
          content: <UserCardCustomActionButtonContainer button={button} chatId={currentChat.chatId} />,
        } as TLayoutUserCardAction);
      }
    });
  }

  if (!actions.length) {
    return null;
  }

  return <UserCardActions actions={actions} />;
};
